import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Badge,
  Form,
  Spinner,
  Pagination,
  InputGroup,
  DropdownButton,
  Dropdown
} from 'react-bootstrap';
import { 
  FaSearch, 
  FaImage, 
  FaVideo, 
  FaPlayCircle,
  FaTimes,
  FaCalendar,
  FaGraduationCap,
  FaBook,
  FaFlask,
  FaFutbol,
  FaMusic,
  FaUsers,
  FaTrophy,
  FaUniversity,
  FaChalkboardTeacher,
  FaBuilding,
  FaFilter,
  FaSort,
  FaDownload,
  FaShareAlt,
  FaHeart,
  FaRegHeart,
  FaEye,
  FaClock,
  FaThLarge,
  FaThList,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaInfoCircle,
  FaEllipsisH,
  FaChevronDown
} from 'react-icons/fa';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const GalleryDisplay = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Likes state
  const [likedItems, setLikedItems] = useState([]);

  // Category mapping with icons and colors
  const categoryConfig = {
    'CAMPUS': { 
      icon: <FaUniversity />, 
      name: 'Campus', 
      color: '#4CAF50',
      bgColor: '#E8F5E9'
    },
    'CLASSROOM': { 
      icon: <FaChalkboardTeacher />, 
      name: 'Classroom', 
      color: '#2196F3',
      bgColor: '#E3F2FD'
    },
    'LABORATORY': { 
      icon: <FaFlask />, 
      name: 'Laboratory', 
      color: '#9C27B0',
      bgColor: '#F3E5F5'
    },
    'LIBRARY': { 
      icon: <FaBook />, 
      name: 'Library', 
      color: '#795548',
      bgColor: '#EFEBE9'
    },
    'SPORTS': { 
      icon: <FaFutbol />, 
      name: 'Sports', 
      color: '#FF5722',
      bgColor: '#FBE9E7'
    },
    'CULTURAL_EVENT': { 
      icon: <FaMusic />, 
      name: 'Cultural Events', 
      color: '#E91E63',
      bgColor: '#FCE4EC'
    },
    'ACADEMIC_EVENT': { 
      icon: <FaGraduationCap />, 
      name: 'Academic Events', 
      color: '#3F51B5',
      bgColor: '#E8EAF6'
    },
    'ANNUAL_FUNCTION': { 
      icon: <FaCalendar />, 
      name: 'Annual Function', 
      color: '#FF9800',
      bgColor: '#FFF3E0'
    },
    'WORKSHOP_SEMINAR': { 
      icon: <FaUsers />, 
      name: 'Workshops', 
      color: '#009688',
      bgColor: '#E0F2F1'
    },
    'EXAMINATION': { 
      icon: <FaBook />, 
      name: 'Examinations', 
      color: '#607D8B',
      bgColor: '#ECEFF1'
    },
    'GRADUATION': { 
      icon: <FaGraduationCap />, 
      name: 'Graduation', 
      color: '#673AB7',
      bgColor: '#EDE7F6'
    },
    'FESTIVAL': { 
      icon: <FaCalendar />, 
      name: 'Festivals', 
      color: '#FF4081',
      bgColor: '#FCE4EC'
    },
    'STUDENT_ACTIVITY': { 
      icon: <FaUsers />, 
      name: 'Student Activities', 
      color: '#00BCD4',
      bgColor: '#E0F7FA'
    },
    'FACULTY': { 
      icon: <FaChalkboardTeacher />, 
      name: 'Faculty', 
      color: '#8BC34A',
      bgColor: '#F1F8E9'
    },
    'INFRASTRUCTURE': { 
      icon: <FaBuilding />, 
      name: 'Infrastructure', 
      color: '#9E9E9E',
      bgColor: '#FAFAFA'
    },
    'ACHIEVEMENTS': { 
      icon: <FaTrophy />, 
      name: 'Achievements', 
      color: '#FFC107',
      bgColor: '#FFFDE7'
    },
    'ADMISSIONS': { 
      icon: <FaUniversity />, 
      name: 'Admissions', 
      color: '#795548',
      bgColor: '#EFEBE9'
    },
    'ORIENTATION': { 
      icon: <FaUsers />, 
      name: 'Orientation', 
      color: '#3F51B5',
      bgColor: '#E8EAF6'
    },
    'ALUMNI_MEET': { 
      icon: <FaUsers />, 
      name: 'Alumni Meet', 
      color: '#607D8B',
      bgColor: '#ECEFF1'
    },
    'OTHER': { 
      icon: <FaInfoCircle />, 
      name: 'Other', 
      color: '#9E9E9E',
      bgColor: '#F5F5F5'
    }
  };

  // Initialize
  useEffect(() => {
    fetchGallery();
    const savedLikes = JSON.parse(localStorage.getItem('galleryLikes') || '[]');
    setLikedItems(savedLikes);
  }, [selectedCategory, selectedType, searchTerm, sortBy]);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      setError('');

      // Build URL with all filters
      let url = `${BASE_URL}/gallery.php`;
      
      const params = [];
      
      if (selectedCategory !== 'all') {
        params.push(`category=${selectedCategory}`);
      }
      
      if (selectedType !== 'all') {
        params.push(`type=${selectedType}`);
      }
      
      if (searchTerm.trim() !== '') {
        params.push(`search=${encodeURIComponent(searchTerm.trim())}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      console.log('Fetching gallery from:', url);

      const response = await fetch(url);
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        let processedItems = result.data;
        processedItems = sortItems(processedItems, sortBy);
        setGalleryItems(processedItems);
      } else {
        setError('No gallery items available');
        setGalleryItems([]);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load gallery');
      setGalleryItems([]);
    } finally {
      setLoading(false);
    }
  };

  const sortItems = (items, sortType) => {
    const sortedItems = [...items];
    
    switch (sortType) {
      case 'newest':
        return sortedItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return sortedItems.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'title_asc':
        return sortedItems.sort((a, b) => a.title.localeCompare(b.title));
      case 'title_desc':
        return sortedItems.sort((a, b) => b.title.localeCompare(a.title));
      default:
        return sortedItems;
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const getYoutubeThumbnail = (url) => {
    if (!url) return null;
    const videoId = url.includes('youtube.com/embed/') 
      ? url.split('youtube.com/embed/')[1]
      : url.includes('youtu.be/') 
        ? url.split('youtu.be/')[1]
        : url.includes('watch?v=')
        ? url.split('watch?v=')[1].split('&')[0]
        : null;
    
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }
    return null;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchGallery();
  };

  const handleResetFilters = () => {
    setSelectedCategory('all');
    setSelectedType('all');
    setSearchTerm('');
    setSortBy('newest');
    setCurrentPage(1);
    fetchGallery();
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setShowModal(false);
  };

  const openLightbox = (item) => {
    if (item.gallery_type === 'photo') {
      setSelectedItem(item);
      setLightboxOpen(true);
    } else {
      handleViewDetails(item);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const handleLike = (itemId) => {
    const newLikedItems = likedItems.includes(itemId)
      ? likedItems.filter(id => id !== itemId)
      : [...likedItems, itemId];
    
    setLikedItems(newLikedItems);
    localStorage.setItem('galleryLikes', JSON.stringify(newLikedItems));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return formatDate(dateString);
  };

  const handleDownload = (item) => {
    if (item.gallery_type === 'photo' && item.image_path) {
      const link = document.createElement('a');
      link.href = getImageUrl(item.image_path);
      link.download = `${item.title.replace(/\s+/g, '_')}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShare = (item) => {
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: item.description || 'Check out this gallery item',
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Filter items based on search term only
  const filteredItems = galleryItems.filter(item => {
    const matchesSearch = searchTerm === '' || 
                         item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Get selected category name
  const getSelectedCategoryName = () => {
    if (selectedCategory === 'all') return 'All Categories';
    return categoryConfig[selectedCategory]?.name || selectedCategory;
  };

  // Get selected category icon
  const getSelectedCategoryIcon = () => {
    if (selectedCategory === 'all') return <FaEllipsisH />;
    return categoryConfig[selectedCategory]?.icon;
  };

  // CSS Styles
  const styles = `
  .gallery-modern {
    --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    --card-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    --card-hover-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Loading State */
  .gallery-loading {
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  }

  .loading-content {
    text-align: center;
  }

  .spinner-modern {
    width: 50px;
    height: 50px;
    border: 3px solid rgba(102, 126, 234, 0.1);
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Hero Banner */
  .gallery-hero-banner {
    position: relative;
    background: var(--primary-gradient);
    padding: 80px 0;
    color: white;
    overflow: hidden;
  }

  .gallery-hero-banner::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
    opacity: 0.1;
  }

  .hero-stats {
    display: flex;
    justify-content: center;
    gap: 40px;
    margin-top: 30px;
  }

  .stat-item {
    text-align: center;
  }

  .stat-item h3 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 5px;
  }

  .stat-item span {
    font-size: 0.9rem;
    opacity: 0.9;
  }

  /* Search Box */
  .search-box {
    max-width: 500px;
    border-radius: 25px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .search-input {
    border: none;
    padding: 12px 20px;
  }

  .search-input:focus {
    box-shadow: none;
  }

  /* Modern Gallery Card */
  .gallery-card-modern {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: var(--card-shadow);
    transition: var(--transition);
    height: 100%;
  }

  .gallery-card-modern:hover {
    transform: translateY(-8px);
    box-shadow: var(--card-hover-shadow);
  }

  .card-image-container {
    position: relative;
    height: 200px;
    overflow: hidden;
    cursor: pointer;
  }

  .card-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }

  .card-image.loaded {
    opacity: 1;
  }

  .gallery-card-modern:hover .card-image {
    transform: scale(1.05);
  }

  .video-thumbnail {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .video-play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: var(--transition);
  }

  .gallery-card-modern:hover .video-play-overlay {
    background: rgba(0, 0, 0, 0.5);
  }

  .card-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 60%, rgba(0, 0, 0, 0.8));
    opacity: 0;
    transition: var(--transition);
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 15px;
    gap: 10px;
  }

  .gallery-card-modern:hover .card-overlay {
    opacity: 1;
  }

  .like-btn, .view-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;
    transition: var(--transition);
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  }

  .like-btn:hover, .view-btn:hover {
    background: #667eea;
    color: white;
    transform: scale(1.1);
  }

  .category-badge {
    position: absolute;
    top: 15px;
    left: 15px;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .video-badge {
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-body {
    padding: 20px;
  }

  .card-title {
    font-weight: 600;
    margin-bottom: 8px;
    color: #333;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-description {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 15px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
  }

  .card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 15px;
    border-top: 1px solid #f1f1f1;
  }

  .type-badge {
    font-size: 0.75rem;
    padding: 4px 8px;
  }

  /* List View */
  .gallery-list-view {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: var(--card-shadow);
  }

  .gallery-list-item-modern {
    display: flex;
    padding: 20px;
    border-bottom: 1px solid #f1f1f1;
    transition: var(--transition);
    cursor: pointer;
  }

  .gallery-list-item-modern:hover {
    background: #f8f9fa;
  }

  .gallery-list-item-modern:last-child {
    border-bottom: none;
  }

  .list-thumbnail {
    flex-shrink: 0;
    width: 150px;
    height: 100px;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    cursor: pointer;
  }

  .list-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .gallery-list-item-modern:hover .list-image {
    transform: scale(1.05);
  }

  .list-video-thumbnail {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .list-video-play {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  .list-content {
    flex: 1;
    margin-left: 20px;
  }

  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .list-title {
    font-weight: 600;
    color: #333;
    margin: 0;
  }

  .list-actions {
    display: flex;
    gap: 8px;
  }

  .list-like-btn, .list-view-btn {
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    transition: var(--transition);
    padding: 4px;
  }

  .list-like-btn:hover, .list-view-btn:hover {
    color: #667eea;
  }

  .list-description {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 15px;
    line-height: 1.4;
  }

  .list-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .list-meta {
    display: flex;
    gap: 10px;
  }

  .list-category-badge, .list-type-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.75rem;
    padding: 4px 8px;
  }

  /* Lightbox */
  .lightbox-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .lightbox-close {
    position: absolute;
    top: 30px;
    right: 30px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    font-size: 1.2rem;
    cursor: pointer;
    transition: var(--transition);
    backdrop-filter: blur(10px);
  }

  .lightbox-close:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(90deg);
  }

  .lightbox-content {
    max-width: 90vw;
    max-height: 90vh;
    position: relative;
    animation: slideUp 0.4s ease;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .lightbox-image {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 10px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }

  .lightbox-info {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
    color: white;
    padding: 30px;
    border-radius: 0 0 10px 10px;
  }

  .lightbox-info h4 {
    margin-bottom: 15px;
  }

  .lightbox-actions {
    display: flex;
    gap: 15px;
  }

  .lightbox-actions button {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: var(--transition);
    backdrop-filter: blur(10px);
  }

  .lightbox-actions button:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  /* Modal Styles */
  .gallery-modal .modal-content {
    border-radius: 20px;
    overflow: hidden;
    border: none;
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3);
  }

  .modal-header {
    border-bottom: 1px solid #f1f1f1;
  }

  .category-icon-modal {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .description-section {
    margin-bottom: 25px;
  }

  .description-section h6 {
    color: #333;
    margin-bottom: 10px;
  }

  .metadata-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    background: #f8f9fa;
    padding: 20px;
    border-radius: 12px;
  }

  .metadata-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .metadata-label {
    font-size: 0.85rem;
    color: #666;
    font-weight: 500;
  }

  .metadata-value {
    font-size: 0.9rem;
    color: #333;
    font-weight: 500;
  }

  .metadata-value.badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    font-size: 0.85rem;
  }

  /* Filter Section */
  .filter-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 25px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border: 1px solid #e9ecef;
  }

  .filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    align-items: flex-end;
  }

  .filter-group {
    flex: 1;
    min-width: 200px;
  }

  .filter-label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
    display: block;
  }

  .filter-select, .filter-input {
    width: 100%;
    padding: 10px 15px;
    border: 1px solid #ced4da;
    border-radius: 8px;
    background: white;
    font-size: 0.95rem;
    transition: all 0.3s ease;
  }

  .filter-select:focus, .filter-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    outline: none;
  }

  .category-dropdown .dropdown-toggle {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    border: 1px solid #ced4da;
    border-radius: 8px;
    background: white;
    text-align: left;
  }

  .category-dropdown .dropdown-toggle:after {
    margin-left: auto;
  }

  .category-dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 15px;
  }

  .category-dropdown-item .category-icon {
    width: 24px;
    text-align: center;
  }

  .category-dropdown-item.active {
    background: #f8f9fa;
  }

  /* Stats Cards */
  .stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 25px 20px;
    text-align: center;
    border: 1px solid #e9ecef;
    transition: all 0.3s ease;
  }

  .stat-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }

  .stat-icon {
    font-size: 2.5rem;
    margin-bottom: 15px;
  }

  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 5px;
  }

  .stat-label {
    color: #6c757d;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .stat-card.photo {
    border-top: 4px solid #4CAF50;
  }

  .stat-card.video {
    border-top: 4px solid #f44336;
  }

  .stat-card.total {
    border-top: 4px solid #667eea;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .hero-stats {
      flex-direction: column;
      gap: 20px;
    }
    
    .gallery-list-item-modern {
      flex-direction: column;
    }
    
    .list-thumbnail {
      width: 100%;
      height: 200px;
      margin-bottom: 15px;
    }
    
    .list-content {
      margin-left: 0;
    }
    
    .list-footer {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
    
    .list-meta {
      flex-wrap: wrap;
    }
    
    .metadata-section {
      grid-template-columns: 1fr;
    }

    .filter-row {
      flex-direction: column;
    }

    .filter-group {
      min-width: 100%;
    }

    .stats-container {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 576px) {
    .gallery-hero-banner {
      padding: 60px 0;
    }
    
    .gallery-hero-banner h1 {
      font-size: 2rem;
    }
    
    .search-box {
      flex-direction: column;
      border-radius: 12px;
    }
    
    .search-input {
      border-radius: 0 !important;
    }
    
    .search-box button {
      border-radius: 0 0 12px 12px !important;
      width: 100%;
    }
  }

  /* Animation for cards */
  @keyframes cardAppear {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .gallery-card-modern {
    animation: cardAppear 0.5s ease forwards;
  }

  .gallery-card-modern:nth-child(2) { animation-delay: 0.1s; }
  .gallery-card-modern:nth-child(3) { animation-delay: 0.2s; }
  .gallery-card-modern:nth-child(4) { animation-delay: 0.3s; }

  /* Empty State */
  .empty-state {
    padding: 60px 30px;
    text-align: center;
    background: white;
    border-radius: 16px;
    box-shadow: var(--card-shadow);
  }

  .empty-state h4 {
    color: #333;
    margin: 20px 0 10px;
  }

  .empty-state p {
    color: #666;
    margin-bottom: 20px;
  }

  /* Custom Scrollbar */
  .gallery-modern::-webkit-scrollbar {
    width: 8px;
  }

  .gallery-modern::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  .gallery-modern::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 4px;
  }

  .gallery-modern::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  }
  `;

  if (loading) {
    return (
      <div className="gallery-loading">
        <div className="loading-content">
          <div className="spinner-modern"></div>
          <h4 className="mt-4">Loading Gallery</h4>
          <p className="text-muted">Discovering beautiful moments...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="gallery-modern">
        {/* Hero Banner */}
        <div className="gallery-hero-banner">
          <div className="hero-overlay"></div>
          <Container>
            <Row className="align-items-center min-vh-50">
              <Col lg={8} className="mx-auto text-center text-white">
                <h1 className="display-4 fw-bold mb-4">Campus Gallery</h1>
                <p className="lead mb-4">
                  Explore our vibrant campus life through photos and videos capturing academic excellence, 
                  cultural events, sports achievements, and memorable moments.
                </p>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="py-5">
          {/* Stats */}
          <div className="stats-container mb-4">
            <div className="stat-card total">
              <div className="stat-icon" style={{ color: '#667eea' }}>
                <FaImage />
              </div>
              <div className="stat-value">{galleryItems.length}</div>
              <div className="stat-label">Total Items</div>
            </div>
            <div className="stat-card photo">
              <div className="stat-icon" style={{ color: '#4CAF50' }}>
                <FaImage />
              </div>
              <div className="stat-value">{galleryItems.filter(i => i.gallery_type === 'photo').length}</div>
              <div className="stat-label">Photos</div>
            </div>
            <div className="stat-card video">
              <div className="stat-icon" style={{ color: '#f44336' }}>
                <FaVideo />
              </div>
              <div className="stat-value">{galleryItems.filter(i => i.gallery_type === 'video').length}</div>
              <div className="stat-label">Videos</div>
            </div>
          </div>

          {/* Combined Filter Section */}
          <div className="filter-section">
            <Row className="align-items-end">
              <Col md={4} className="mb-3 mb-md-0">
                <Form onSubmit={handleSearch}>
                  <Form.Label className="filter-label">Search Gallery</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search by title or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                    <Button type="submit" variant="primary">
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </Form>
              </Col>
              
              <Col md={3} className="mb-3 mb-md-0">
                <div className="filter-group category-dropdown">
                  <label className="filter-label">Category</label>
                  <Dropdown>
                    <Dropdown.Toggle variant="light" className="filter-select">
                      <span className="d-flex align-items-center gap-2">
                        {getSelectedCategoryIcon()}
                        {getSelectedCategoryName()}
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      <Dropdown.Item 
                        onClick={() => setSelectedCategory('all')}
                        className={`category-dropdown-item ${selectedCategory === 'all' ? 'active' : ''}`}
                      >
                        <span className="category-icon">
                          <FaEllipsisH />
                        </span>
                        <span>All Categories</span>
                      </Dropdown.Item>
                      {Object.entries(categoryConfig).map(([key, category]) => (
                        <Dropdown.Item 
                          key={key}
                          onClick={() => setSelectedCategory(key)}
                          className={`category-dropdown-item ${selectedCategory === key ? 'active' : ''}`}
                        >
                          <span className="category-icon" style={{ color: category.color }}>
                            {category.icon}
                          </span>
                          <span>{category.name}</span>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Col>
              
              <Col md={3} className="mb-3 mb-md-0">
                <div className="filter-group">
                  <label className="filter-label">Media Type</label>
                  <select 
                    className="filter-select"
                    value={selectedType} 
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="photo">Photos Only</option>
                    <option value="video">Videos Only</option>
                  </select>
                </div>
              </Col>
              
              <Col md={2}>
                <div className="filter-group">
                  <label className="filter-label">View</label>
                  <div className="d-flex gap-2">
                    <Button
                      variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                      onClick={() => setViewMode('grid')}
                      size="sm"
                      className="w-50"
                    >
                      <FaThLarge />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                      onClick={() => setViewMode('list')}
                      size="sm"
                      className="w-50"
                    >
                      <FaThList />
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
            
            <Row className="mt-3">
              <Col>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex gap-2">
                    <DropdownButton
                      variant="outline-secondary"
                      size="sm"
                      title={<><FaSort className="me-1" /> Sort By</>}
                    >
                      <Dropdown.Item 
                        active={sortBy === 'newest'}
                        onClick={() => setSortBy('newest')}
                      >
                        Newest First
                      </Dropdown.Item>
                      <Dropdown.Item 
                        active={sortBy === 'oldest'}
                        onClick={() => setSortBy('oldest')}
                      >
                        Oldest First
                      </Dropdown.Item>
                      <Dropdown.Item 
                        active={sortBy === 'title_asc'}
                        onClick={() => setSortBy('title_asc')}
                      >
                        Title A-Z
                      </Dropdown.Item>
                      <Dropdown.Item 
                        active={sortBy === 'title_desc'}
                        onClick={() => setSortBy('title_desc')}
                      >
                        Title Z-A
                      </Dropdown.Item>
                    </DropdownButton>
                  </div>
                  
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={handleResetFilters}
                  >
                    Reset Filters
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* Active Filters Display */}
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <small className="text-muted me-2">Active filters:</small>
              {selectedCategory !== 'all' && (
                <Badge 
                  pill 
                  className="d-flex align-items-center gap-1"
                  style={{ 
                    backgroundColor: categoryConfig[selectedCategory]?.bgColor,
                    color: categoryConfig[selectedCategory]?.color
                  }}
                >
                  {categoryConfig[selectedCategory]?.icon}
                  {categoryConfig[selectedCategory]?.name}
                </Badge>
              )}
              {selectedType !== 'all' && (
                <Badge pill bg={selectedType === 'photo' ? 'info' : 'danger'}>
                  {selectedType === 'photo' ? 'Photos Only' : 'Videos Only'}
                </Badge>
              )}
              {searchTerm && (
                <Badge pill bg="secondary">
                  Search: "{searchTerm}"
                </Badge>
              )}
            </div>
          </div>

          {/* Gallery Content */}
          {error ? (
            <div className="text-center py-5">
              <div className="empty-state">
                <FaImage size={48} className="text-muted mb-3" />
                <h4>No Gallery Items Found</h4>
                <p className="text-muted">{error}</p>
                <Button variant="primary" onClick={handleResetFilters}>
                  Reset Filters
                </Button>
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-5">
              <div className="empty-state">
                <FaImage size={48} className="text-muted mb-3" />
                <h4>No Gallery Items Found</h4>
                <p className="text-muted">Try adjusting your filters or search terms</p>
                <Button variant="primary" onClick={handleResetFilters}>
                  Reset Filters
                </Button>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            <Row className="gallery-grid">
              {currentItems.map((item) => (
                <Col key={item.id} xl={3} lg={4} md={6} className="mb-4">
                  <GalleryCard 
                    item={item}
                    onViewDetails={handleViewDetails}
                    onLike={handleLike}
                    onOpenLightbox={openLightbox}
                    isLiked={likedItems.includes(item.id)}
                    getImageUrl={getImageUrl}
                    getYoutubeThumbnail={getYoutubeThumbnail}
                    categoryConfig={categoryConfig}
                    formatTimeAgo={formatTimeAgo}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="gallery-list-view">
              {currentItems.map((item) => (
                <GalleryListItem 
                  key={item.id}
                  item={item}
                  onViewDetails={handleViewDetails}
                  onLike={handleLike}
                  onOpenLightbox={openLightbox}
                  isLiked={likedItems.includes(item.id)}
                  getImageUrl={getImageUrl}
                  getYoutubeThumbnail={getYoutubeThumbnail}
                  categoryConfig={categoryConfig}
                  formatTimeAgo={formatTimeAgo}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {filteredItems.length > itemsPerPage && (
            <div className="mt-5">
              <Pagination className="justify-content-center">
                <Pagination.Prev 
                  onClick={() => paginate(currentPage - 1)} 
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Previous
                </Pagination.Prev>
                
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index + 1}
                    active={index + 1 === currentPage}
                    onClick={() => paginate(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                
                <Pagination.Next 
                  onClick={() => paginate(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                >
                  Next <FaChevronRight />
                </Pagination.Next>
              </Pagination>
              
              <div className="text-center mt-2 text-muted">
                Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredItems.length)} of {filteredItems.length} items
              </div>
            </div>
          )}
        </Container>

        {/* Lightbox */}
        {lightboxOpen && selectedItem && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <FaTimes />
            </button>
            <div className="lightbox-content" onClick={e => e.stopPropagation()}>
              <img 
                src={getImageUrl(selectedItem.image_path)} 
                alt={selectedItem.title}
                className="lightbox-image"
              />
              <div className="lightbox-info">
                <h4>{selectedItem.title}</h4>
                <div className="lightbox-actions">
                  <button onClick={() => handleLike(selectedItem.id)}>
                    {likedItems.includes(selectedItem.id) ? <FaHeart className="text-danger" /> : <FaRegHeart />}
                  </button>
                  <button onClick={() => handleDownload(selectedItem)}>
                    <FaDownload />
                  </button>
                  <button onClick={() => handleShare(selectedItem)}>
                    <FaShareAlt />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {selectedItem && (
          <Modal 
            show={showModal} 
            onHide={handleCloseModal} 
            centered 
            size="lg"
            className="gallery-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                <div className="d-flex align-items-center gap-3">
                  <div 
                    className="category-icon-modal"
                    style={{ 
                      backgroundColor: categoryConfig[selectedItem.category]?.bgColor || '#f8f9fa',
                      color: categoryConfig[selectedItem.category]?.color || '#6c757d'
                    }}
                  >
                    {categoryConfig[selectedItem.category]?.icon}
                  </div>
                  <div>
                    <h5 className="mb-0">{selectedItem.title}</h5>
                    <small className="text-muted">
                      {categoryConfig[selectedItem.category]?.name} • {formatDate(selectedItem.created_at)}
                    </small>
                  </div>
                </div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-4">
                {selectedItem.gallery_type === 'photo' ? (
                  <img
                    src={getImageUrl(selectedItem.image_path)}
                    alt={selectedItem.title}
                    className="img-fluid rounded w-100"
                    style={{ maxHeight: '400px', objectFit: 'cover' }}
                  />
                ) : (
                  <div className="ratio ratio-16x9">
                    <iframe
                      src={selectedItem.video_url}
                      title={selectedItem.title}
                      allowFullScreen
                      className="rounded"
                    ></iframe>
                  </div>
                )}
              </div>
              
              {selectedItem.description && (
                <div className="description-section">
                  <h6>Description</h6>
                  <p className="text-muted">{selectedItem.description}</p>
                </div>
              )}
              
              <div className="metadata-section">
                <div className="metadata-item">
                  <span className="metadata-label">Category:</span>
                  <Badge 
                    className="metadata-value"
                    style={{ 
                      backgroundColor: categoryConfig[selectedItem.category]?.bgColor,
                      color: categoryConfig[selectedItem.category]?.color
                    }}
                  >
                    {categoryConfig[selectedItem.category]?.icon}
                    {categoryConfig[selectedItem.category]?.name}
                  </Badge>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Type:</span>
                  <Badge 
                    bg={selectedItem.gallery_type === 'photo' ? 'info' : 'danger'}
                    className="metadata-value"
                  >
                    {selectedItem.gallery_type === 'photo' ? 'Photo' : 'Video'}
                  </Badge>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Uploaded:</span>
                  <span className="metadata-value">
                    <FaClock className="me-1" />
                    {formatTimeAgo(selectedItem.created_at)}
                  </span>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex justify-content-between w-100 align-items-center">
                <Button
                  variant={likedItems.includes(selectedItem.id) ? "danger" : "outline-danger"}
                  onClick={() => handleLike(selectedItem.id)}
                  size="sm"
                >
                  {likedItems.includes(selectedItem.id) ? <FaHeart /> : <FaRegHeart />}
                  {likedItems.includes(selectedItem.id) ? ' Liked' : ' Like'}
                </Button>
                <div>
                  {selectedItem.gallery_type === 'photo' && (
                    <Button
                      variant="outline-primary"
                      onClick={() => handleDownload(selectedItem)}
                      className="me-2"
                      size="sm"
                    >
                      <FaDownload /> Download
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    onClick={handleCloseModal}
                    className="me-2"
                    size="sm"
                  >
                    Close
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      handleCloseModal();
                      if (selectedItem.gallery_type === 'photo') {
                        openLightbox(selectedItem);
                      }
                    }}
                    size="sm"
                  >
                    <FaExpand className="me-1" />
                    {selectedItem.gallery_type === 'photo' ? 'Full Screen' : 'Play Video'}
                  </Button>
                </div>
              </div>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </>
  );
};

// Gallery Card Component (Grid View)
const GalleryCard = ({ 
  item, 
  onViewDetails, 
  onLike, 
  onOpenLightbox,
  isLiked,
  getImageUrl,
  getYoutubeThumbnail,
  categoryConfig,
  formatTimeAgo
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <div className="gallery-card-modern">
      <div 
        className="card-image-container"
        onClick={() => item.gallery_type === 'photo' ? onOpenLightbox(item) : onViewDetails(item)}
      >
        {item.gallery_type === 'photo' ? (
          <img
            src={getImageUrl(item.image_path)}
            alt={item.title}
            className={`card-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
          />
        ) : (
          <div className="video-thumbnail">
            <img
              src={getYoutubeThumbnail(item.video_url) || 'https://via.placeholder.com/400x250?text=Video'}
              alt={item.title}
              className="card-image"
            />
            <div className="video-play-overlay">
              <FaPlayCircle size={32} />
            </div>
          </div>
        )}
        
        <div className="card-overlay">
          <button 
            className="like-btn"
            onClick={(e) => {
              e.stopPropagation();
              onLike(item.id);
            }}
          >
            {isLiked ? <FaHeart className="text-danger" /> : <FaRegHeart />}
          </button>
          <button 
            className="view-btn"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(item);
            }}
          >
            <FaEye />
          </button>
        </div>
        
        <div 
          className="category-badge"
          style={{ 
            backgroundColor: categoryConfig[item.category]?.bgColor,
            color: categoryConfig[item.category]?.color
          }}
        >
          {categoryConfig[item.category]?.icon}
        </div>
        
        {item.gallery_type === 'video' && (
          <div className="video-badge">
            <FaVideo size={12} />
          </div>
        )}
      </div>
      
      <div className="card-body">
        <h6 className="card-title">{item.title}</h6>
        <p className="card-description">
          {item.description && item.description.length > 100 
            ? item.description.substring(0, 100) + '...' 
            : item.description}
        </p>
        <div className="card-footer">
          <small className="text-muted">
            <FaClock className="me-1" />
            {formatTimeAgo(item.created_at)}
          </small>
          <Badge 
            className="type-badge"
            bg={item.gallery_type === 'photo' ? 'info' : 'danger'}
          >
            {item.gallery_type === 'photo' ? 'Photo' : 'Video'}
          </Badge>
        </div>
      </div>
    </div>
  );
};

// Gallery List Item Component (List View)
const GalleryListItem = ({ 
  item, 
  onViewDetails, 
  onLike, 
  onOpenLightbox,
  isLiked,
  getImageUrl,
  getYoutubeThumbnail,
  categoryConfig,
  formatTimeAgo
}) => {
  return (
    <div className="gallery-list-item-modern">
      <div 
        className="list-thumbnail"
        onClick={() => item.gallery_type === 'photo' ? onOpenLightbox(item) : onViewDetails(item)}
      >
        {item.gallery_type === 'photo' ? (
          <img
            src={getImageUrl(item.image_path)}
            alt={item.title}
            className="list-image"
          />
        ) : (
          <div className="list-video-thumbnail">
            <img
              src={getYoutubeThumbnail(item.video_url) || 'https://via.placeholder.com/150x100?text=Video'}
              alt={item.title}
              className="list-image"
            />
            <div className="list-video-play">
              <FaPlayCircle size={16} />
            </div>
          </div>
        )}
      </div>
      
      <div className="list-content">
        <div className="list-header">
          <h6 className="list-title">{item.title}</h6>
          <div className="list-actions">
            <button 
              className="list-like-btn"
              onClick={() => onLike(item.id)}
            >
              {isLiked ? <FaHeart className="text-danger" /> : <FaRegHeart />}
            </button>
            <button 
              className="list-view-btn"
              onClick={() => onViewDetails(item)}
            >
              <FaEye />
            </button>
          </div>
        </div>
        
        <p className="list-description">
          {item.description || 'No description available'}
        </p>
        
        <div className="list-footer">
          <div className="list-meta">
            <Badge 
              className="list-category-badge"
              style={{ 
                backgroundColor: categoryConfig[item.category]?.bgColor,
                color: categoryConfig[item.category]?.color
              }}
            >
              {categoryConfig[item.category]?.icon}
              <span>{categoryConfig[item.category]?.name}</span>
            </Badge>
            <Badge 
              className="list-type-badge"
              bg={item.gallery_type === 'photo' ? 'info' : 'danger'}
            >
              {item.gallery_type === 'photo' ? 'Photo' : 'Video'}
            </Badge>
          </div>
          <small className="text-muted">
            <FaClock className="me-1" />
            {formatTimeAgo(item.created_at)}
          </small>
        </div>
      </div>
    </div>
  );
};

export default GalleryDisplay;